'use client';

import { useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';

type ImgItem = { url: string; caption?: string | null; alt?: string | null };

/** 'W / H' 비율 문자열 → 폭 400 기준 정수 치수. 해석 실패 시 4:3(400×300). */
export function thumbDims(aspectRatio: string): [number, number] {
  const m = /^\s*(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)\s*$/.exec(String(aspectRatio ?? ''));
  const w = m ? Number(m[1]) : 4;
  const h = m ? Number(m[2]) : 3;
  if (!(w > 0) || !(h > 0)) return [400, 300];
  return [400, Math.round((400 * h) / w)];
}

export function ImageLightbox({
  images,
  columns = 3,
  aspectRatio = '4 / 3',
}: {
  images: ImgItem[];
  columns?: 2 | 3;
  aspectRatio?: string;
}) {
  const [index, setIndex] = useState(-1);
  if (!images?.length) return null;

  const gridCls = columns === 2 ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-3';
  // ㉑c §3-3 — 고유 치수 속성. 상자는 aspectRatio 가 잡고 그림은 cover 로 채우므로 속성은 «비율 힌트» 다(CLS 0).
  const [iw, ih] = thumbDims(aspectRatio);

  return (
    <>
      <div
        className={`grid ${gridCls} gap-2`}
        style={{ display: 'grid', gridTemplateColumns: columns === 2 ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)', gap: 8 }}
      >
        {images.map((img, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`이미지 ${i + 1} 확대 보기`}
            style={{
              overflow: 'hidden',
              borderRadius: 8,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              padding: 0,
              cursor: 'zoom-in',
              aspectRatio,
              display: 'block',
            }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={img.url}
              alt={img.alt ?? img.caption ?? `이미지 ${i + 1}`}
              width={iw}
              height={ih}
              loading="lazy"
              decoding="async"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              onError={(e) => {
                const el = e.currentTarget as HTMLImageElement;
                el.style.visibility = 'hidden';
              }}
            />
          </button>
        ))}
      </div>
      <Lightbox
        open={index >= 0}
        close={() => setIndex(-1)}
        index={Math.max(0, index)}
        slides={images.map((img) => ({
          src: img.url,
          alt: img.alt ?? img.caption ?? undefined,
          description: img.caption ?? undefined,
        }))}
        plugins={[Zoom, Counter]}
        controller={{ closeOnBackdropClick: true }}
        zoom={{ maxZoomPixelRatio: 3 }}
      />
    </>
  );
}

export default ImageLightbox;
